import { api, acct } from "./client";
import type { ListQuery } from "./types";

/**
 * The standard crossbar collection: GET list, PUT create (402-able), GET,
 * POST replace, PATCH merge, DELETE. Resource modules start from this and
 * add the endpoints that are specific to them.
 */
export interface Doc {
  id: string;
  name?: string;
  [key: string]: unknown;
}

type PatchLeaf<V> = V extends (infer U)[] ? U[] | null : V extends object ? Patch<V> | null : V | null;
/** `unknown` stays `unknown` (index signatures like `[key: string]: unknown`); everything else may also be `null`. */
type PatchValue<V> = unknown extends V ? unknown : PatchLeaf<NonNullable<V>>;
/**
 * A crossbar PATCH body: any subset of the document, nested to any depth,
 * where `null` on a key deletes it (kz_json:merge_recursive semantics).
 */
export type Patch<T> = { [K in keyof T]?: PatchValue<T[K]> };

export function crud<TSummary extends Doc, TDoc extends Doc = TSummary>(noun: string) {
  return {
    noun,
    list: (accountId: string, q?: ListQuery) => api.list<TSummary>(acct(accountId, noun), { paginate: false, ...q }),
    get: (accountId: string, id: string) => api.get<TDoc>(acct(accountId, noun, id)),
    create: (accountId: string, doc: Partial<TDoc>, acceptCharges = false) => api.put<TDoc>(acct(accountId, noun), doc, { acceptCharges }),
    /** Full replace. Prefer `patch` for partial edits. */
    update: (accountId: string, id: string, doc: TDoc) => api.post<TDoc>(acct(accountId, noun, id), doc),
    patch: (accountId: string, id: string, patch: Patch<TDoc>) => api.patch<TDoc>(acct(accountId, noun, id), patch),
    remove: (accountId: string, id: string) => api.delete(acct(accountId, noun, id)),
    path: (accountId: string, ...rest: (string | number)[]) => acct(accountId, noun, ...rest),
  };
}
